import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Link, Typography } from '@mui/material';
import logo from '../../assets/Logo.avif';
import { COMPANY, EMERGENCY, SOCIAL_LINKS, SUPPORT } from '../../content/site';
import { CONTENT_WIDTH, PHI } from '../../styles/golden';
import { BRAND } from '../../styles/ui';

const linkSx = {
  color: BRAND.ink,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' }
};

const FooterHeading: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    variant="subtitle2"
    component="h2"
    sx={{ color: BRAND.muted, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem', mb: 2 }}
  >
    {children}
  </Typography>
);

const FooterLink: React.FC<{ to: string; children: ReactNode }> = ({ to, children }) => (
  <Link component={RouterLink} to={to} variant="body2" sx={{ ...linkSx, display: 'block', mb: 1.5 }}>
    {children}
  </Link>
);

// Site footer: brand + safety note, support details, site links, legal line
const SiteFooter: React.FC = () => (
  <Box
    component="footer"
    sx={{
      backgroundColor: 'var(--joan-bg-tint)',
      borderTop: `1px solid ${BRAND.line}`,
      color: BRAND.ink,
      px: { xs: 3, md: 5 },
      pt: { xs: 5, md: 6 },
      pb: 'calc(34px + env(safe-area-inset-bottom))'
    }}
  >
    <Box sx={{ maxWidth: CONTENT_WIDTH.page, mx: 'auto' }}>
      {/* Columns split φ : 1 : 1 on desktop */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: `${PHI}fr 1fr 1fr` },
          gap: { xs: 4, md: 5 }
        }}
      >
        <Box sx={{ gridColumn: { sm: '1 / -1', md: 'auto' } }}>
          <Box component={RouterLink} to="/" aria-label="Joan home" sx={{ display: 'inline-flex', mb: 2 }}>
            <Box component="img" src={logo} alt="Joan" sx={{ height: 29 }} />
          </Box>
          <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3, maxWidth: 377 }}>
            Raise the alarm. Without making a sound.
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 377 }}>
            <strong>In danger? Call 999.</strong> Joan is not a personal alarm and cannot contact the emergency services.
          </Typography>
        </Box>

        <Box>
          <FooterHeading>Support</FooterHeading>
          <Link href={SUPPORT.phoneHref} variant="body1" sx={{ ...linkSx, fontWeight: 600, display: 'block' }}>
            {SUPPORT.phone}
          </Link>
          <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1.5 }}>
            {SUPPORT.hours}
          </Typography>
          <Link href={`mailto:${SUPPORT.email}`} variant="body2" sx={{ ...linkSx, display: 'block', mb: 1.5 }}>
            {SUPPORT.email}
          </Link>
          <FooterLink to="/contact">Contact and help</FooterLink>
        </Box>

        <Box>
          <FooterHeading>Joan</FooterHeading>
          <FooterLink to="/#how-it-works">How it works</FooterLink>
          <FooterLink to="/services">Local support</FooterLink>
          <FooterLink to="/privacy">Privacy policy</FooterLink>
          <FooterLink to="/terms">Terms of use</FooterLink>
        </Box>
      </Box>

      <Divider sx={{ borderColor: BRAND.line, my: { xs: 4, md: 5 } }} />

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" sx={{ color: BRAND.muted }}>
          © {new Date().getFullYear()} {COMPANY.name}. Registered in England and Wales, company no. {COMPANY.number}.
        </Typography>
        <Box component="nav" aria-label="Social media" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {SOCIAL_LINKS.map(({ label, href }) => (
            <Link key={label} href={href} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ ...linkSx, color: BRAND.muted }}>
              {label}
            </Link>
          ))}
        </Box>
      </Box>

      <Typography variant="caption" component="p" sx={{ color: BRAND.faint, mt: 2 }}>
        {EMERGENCY.helplineName}: <Link href={EMERGENCY.helplineHref} sx={{ ...linkSx, color: BRAND.faint }}>{EMERGENCY.helplinePhone}</Link>
      </Typography>
    </Box>
  </Box>
);

// Compact links for the sign-in screens, where a full footer doesn't fit
export const LegalLinks: React.FC = () => (
  <Box component="nav" aria-label="Help and legal" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 5 }}>
    {[
      { to: '/contact', label: 'Contact and help' },
      { to: '/privacy', label: 'Privacy policy' },
      { to: '/terms', label: 'Terms of use' }
    ].map(({ to, label }) => (
      <Link key={to} component={RouterLink} to={to} variant="body2" sx={{ ...linkSx, color: BRAND.muted }}>
        {label}
      </Link>
    ))}
  </Box>
);

export default SiteFooter;
